import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, FileText, Filter } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { FileStatusBadge } from '../../components/features/FileStatusBadge';
import { getHistory } from '../../api/files';
import { useAuthStore } from '../../store/auth.store';
import type { FileStatus } from '../../api/types';

// Клиентский фильтр: сервер не поддерживает фильтрацию по статусу, фильтруем
// результат пагинированной страницы в браузере. Для полноценной фильтрации
// нужен бэкенд-параметр.
const STATUS_FILTERS: Array<{ value: '' | FileStatus; label: string }> = [
  { value: '', label: 'Все' },
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'SELECT_PAGES', label: 'Выбор страниц' },
  { value: 'COMPLETED', label: 'Готово' },
  { value: 'FAILED', label: 'С ошибкой' },
];

export function FilesPage() {
  const userGUID = useAuthStore((s) => s.user?.guid);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'' | FileStatus>('');
  const limit = 20;

  const query = useQuery({
    queryKey: ['history', userGUID, page],
    queryFn: () => {
      if (!userGUID) throw new Error('No user');
      return getHistory(userGUID, { page, limit });
    },
    enabled: Boolean(userGUID),
  });

  const filtered = useMemo(() => {
    if (!query.data) return [];
    if (!statusFilter) return query.data.files;
    return query.data.files.filter((f) => f.status === statusFilter);
  }, [query.data, statusFilter]);

  const totalPages = query.data?.pagination.totalPages ?? 1;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">История файлов</h1>
        <div className="flex items-center gap-1 overflow-x-auto">
          <Filter size={16} className="text-text-muted" />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              className={
                statusFilter === f.value
                  ? 'rounded-full bg-bg-soft px-3 py-1.5 text-sm font-medium text-brand-primary'
                  : 'rounded-full px-3 py-1.5 text-sm text-text-muted hover:text-text'
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-[16px]" />
          ))}
        </div>
      ) : filtered.length > 0 ? (
        <>
          <Card className="flex flex-col gap-2 p-3 md:p-4">
            <div className="hidden grid-cols-[1fr_180px_100px_140px] gap-3 border-b border-stroke pb-2 text-xs font-medium uppercase tracking-wide text-text-muted md:grid">
              <span>Файл</span>
              <span>Загружен</span>
              <span>Страниц</span>
              <span>Статус</span>
            </div>
            <ul className="flex flex-col gap-2">
              {filtered.map((f, i) => (
                <li
                  key={f.guid}
                  className="animate-enter"
                  style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}
                >
                  <Link
                    to={`/files/${f.guid}`}
                    className="press-down grid grid-cols-1 gap-1 rounded-[14px] border border-stroke bg-white px-3 py-3 transition-all duration-200 ease-[var(--ease-out-soft)] hover:-translate-y-0.5 hover:border-brand-primary hover:shadow-soft md:grid-cols-[1fr_180px_100px_140px] md:items-center md:gap-3 md:border-transparent md:px-2 md:hover:translate-y-0 md:hover:shadow-none md:hover:bg-bg-soft/40"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText size={16} className="text-brand-primary" />
                      <span className="truncate text-sm font-medium">
                        {f.alias}
                      </span>
                    </div>
                    <span className="text-xs tabular-nums text-text-muted md:text-sm">
                      {f.date_added}
                    </span>
                    <span className="text-xs tabular-nums text-text-muted md:text-sm">
                      {f.pages_select}
                    </span>
                    <span>
                      <FileStatusBadge status={f.status} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <Button
                variant="secondary"
                size="sm"
                disabled={!query.data?.pagination.hasPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                leftIcon={<ChevronLeft size={16} />}
              >
                Назад
              </Button>
              <span className="text-sm text-text-muted">
                Страница {page} из {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={!query.data?.pagination.hasNext}
                onClick={() => setPage((p) => p + 1)}
                rightIcon={<ChevronRight size={16} />}
              >
                Далее
              </Button>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon={<FileText size={22} />}
          title={statusFilter ? 'Нет файлов с таким статусом' : 'История пуста'}
          description={
            statusFilter
              ? 'Сбросьте фильтр или загрузите новый файл.'
              : 'Загрузите первый файл с дашборда — результат появится здесь.'
          }
          action={
            <Link to="/dashboard">
              <Button>На дашборд</Button>
            </Link>
          }
        />
      )}
    </div>
  );
}
